/*
 * Copyright (c) 2020. American Express Company
 */

import { LocalDate } from '@js-joda/core';

import { Board } from 'src/bw/board/Board';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { FullUserModel } from 'src/types/user.model';
import CashFlowService from '../../services/cashflow.service';
import StorageService from '../../services/storage.service';
import TimeService from '../../services/time.service';
import UserService from '../../services/user.service';
import UtilsService from '../../services/utils.service';
import { FREECASH_VISIBLE } from 'src/utils/constants';

export interface GraphRanges {
    topMax: number;
    bottomMax: number;
    total: number;
    max: number;
    highlighted?: { type: string; offset: number };
    showWorkOpen?: boolean;
}

export default class CalcFactory {
    private readonly component: Board;

    constructor(component: any) {
        this.component = component;
    }

    public async calculateNetworth(ageCursor: number, user: FullUserModel = null): Promise<void> {
        if (!user) {
            user = this.component.props.user;
        }

        if (!user || !user.cashFlow || user.cashFlow.length === 0) {
            return;
        }

        try {
            const netWorth = Math.round(Math.ceil(this.getNetWorthAtAge(ageCursor, user) / 10) * 10);
            const oldNetWorth = Number(Math.round(Math.ceil(this.component.state.netWorth) / 10) * 10);
            if (netWorth === oldNetWorth) return;

            this.component.setState({
                currentNetWorth: user.currentNetWorth,
                oldNetWorth,
                netWorth
            });

            await UserService.putAttributes({
                networth: user.currentNetWorth
            });
        } catch (e) {
            console.log('error ', e);
        }
    }

    /**
     * Calculate the NetWorth at the given CashFlowModel.
     * This is a super simple calculation but it seems in a bit of flux so let's just do it in one
     * place -- here.
     */
    public static getNetWorth(cashFlow: CashFlowModel): number {
        return UtilsService.getNetWorth(cashFlow);
    }

    /** Get the net worth at the given age */
    private getNetWorthAtAge(ageCursor: number, user: FullUserModel): number {
        let netWorth: number = 0;

        const ageOffset: number = this.getAgeOffset(ageCursor, user.dob);
        if (ageOffset === 0) {
            // use currentNetWorth from Node if the age cursor is on today
            netWorth = user.currentNetWorth;
        } else {
            // otherwise calculate the netWorth from cashFlow
            const offset: number = ageOffset + CashFlowService.getCashFlowIndexWithDate(user.cashFlow, LocalDate.now());
            netWorth = CalcFactory.getNetWorth(user.cashFlow[offset]);
        }
        return netWorth;
    }

    /** Find the right offset into the cashflow, the timeline may be scrolled */
    private getAgeOffset(ageCursor: number, dob: LocalDate): number {
        if (ageCursor && dob) {
            // number of cashflows per age depends on zoom
            const zoomMultiplier: number = 12 / StorageService.getMonthsPerPeriod();
            const age = TimeService.getYearsOld(dob);
            return zoomMultiplier * (ageCursor - age);
        } else {
            return 0;
        }
    }

    /** Find the maximum networth in the user's cashFlow, as an absolute value (positive or negative)
     * @return [maximum, minimum] net worth, with both as positive numbers
     * @args user.cashFlow array of CashFlowModel's
     */
    public static getMaximumMinimumNetWorth(cashFlow: CashFlowModel[]): [number, number] {
        return cashFlow.reduce(
            ([max, min], cf) => {
                const netWorth = this.getNetWorth(cf);
                if (netWorth >= 0) {
                    return [Math.max(max, netWorth), min];
                } else {
                    return [max, Math.max(min, -netWorth)];
                }
            },
            [0, 0]
        );
    }

    /** Find the maximum income/expense in the user's cashFlow, as an absolucte value (positive or negative)
     * @args user.cashFlow array of CashFlowModel's
     */
    public static getMaximumIncomeExpense(cashFlow: CashFlowModel[]): [number, number] {
        return cashFlow.reduce(
            ([maxIncome, maxExpense], cf) => [
                Math.max(maxIncome, cf.income),
                Math.max(maxExpense, Math.abs(cf.expense))
            ],
            [0, 0]
        );
    }

    /** Find the maximum investment/debt in the user's cashFlow, as an absolute value (positive or negative)
     * user.cashFlow array of CashFlowModel's
     */
    public static getMaximumInvestmentDebt(cashFlow: CashFlowModel[]): [number, number] {
        if (FREECASH_VISIBLE) {
            return cashFlow.reduce(
                ([maxInvestment, maxDebt], cf) => [
                    Math.max(maxInvestment, cf.freeCash > 0 ? cf.investment + cf.freeCash : cf.investment),
                    Math.max(maxDebt, Math.abs(cf.freeCash < 0 ? cf.debt + cf.freeCash : cf.debt))
                ],
                [0, 0]
            );
        } else {
            return cashFlow.reduce(
                ([maxInvestment, maxDebt], cf) => [
                    Math.max(maxInvestment, cf.investment),
                    Math.max(maxDebt, Math.abs(cf.debt))
                ],
                [0, 0]
            );
        }
    }

    /** Helper function for graph scaling. The two numbers will be set to no smaller than 1/4 of the largest. */
    public static getGraphRanges(topMax: number, bottomMax: number): GraphRanges {
        const max: number = Math.max(topMax, bottomMax);
        const quarter: number = max / 4;

        if (topMax > bottomMax) {
            bottomMax = (Math.trunc(bottomMax / quarter) + 1) * quarter;
        } else {
            topMax = (Math.trunc(topMax / quarter) + 1) * quarter;
        }
        return {
            topMax,
            bottomMax,
            total: topMax + bottomMax,
            max
        };
    }
}
